import {
  AppLayout,
  Button,
  Checkbox,
  Container,
  ContentLayout,
  Flashbar,
  FormField,
  Header,
  HelpPanel,
  SpaceBetween,
  Textarea,
  TextContent,
} from '@amzn/awsui-components-react';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AmbassadorOffboardingHelp } from './ambassadorOffboardingHelp';
import { useOffboardAmbassador } from '../../hooks/ambassadors/ambassadorCreation/offboardAmbassadorMutation';
import { useUser } from '../../hooks/user';
import { AmbassadorSelect } from '../attendance/editEvent/ambassadors';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { publishKatalMetric } from '../katalAnalytics';

interface AmbassadorOffboardingFormProps {
  setSubmitted: Dispatch<SetStateAction<boolean>>;
}

export function AmbassadorOffboardingForm({ setSubmitted }: AmbassadorOffboardingFormProps) {
  const { t } = useTranslation(['ambassadors', 'translation']);
  const loggedInUser = useUser();

  const [failed, setFailed] = useState(false);

  const [tooTimeConsuming, setTooTimeConsuming] = useState<string | undefined>(undefined);
  const [rotatesAmbassadors, setRotatesAmbassadors] = useState<string | undefined>(undefined);
  const [iDontEnjoyThisRole, setIDontEnjoyThisRole] = useState<string | undefined>(undefined);
  const [imChangingSiteTeamOrElg, setImChangingSiteTeamOrElg] = useState<string | undefined>(
    undefined
  );
  const [leavingForOtherReason, setLeavingForOtherReason] = useState<string | undefined>(undefined);
  const [selectedReplacement, setSelectedReplacement] = useState<OptionDefinition | undefined>(
    undefined
  );

  const [feedback, setFeedback] = useState('');
  const [whyGoodFit, setWhyGoodFit] = useState<string | undefined>('');

  const offboardAmbassador = useOffboardAmbassador();

  const isValid = useMemo(() => {
    const hasPickedAReason =
      tooTimeConsuming ||
      rotatesAmbassadors ||
      iDontEnjoyThisRole ||
      imChangingSiteTeamOrElg ||
      leavingForOtherReason;

    return hasPickedAReason;
  }, [
    tooTimeConsuming,
    rotatesAmbassadors,
    iDontEnjoyThisRole,
    imChangingSiteTeamOrElg,
    leavingForOtherReason,
  ]);

  const isLoading = offboardAmbassador.isPending;

  const onSubmit = () => {
    const offboardingReasons = [
      tooTimeConsuming,
      rotatesAmbassadors,
      iDontEnjoyThisRole,
      imChangingSiteTeamOrElg,
      leavingForOtherReason,
    ].filter(Boolean) as string[];

    if (loggedInUser.employeeId) {
      const offboardRequest = {
        employeeId: loggedInUser.employeeId,
        offboardingReasons: offboardingReasons,
        offboardingAdditionalFeedback: feedback,
        offboardingNomineeAlias: selectedReplacement?.description,
        offboardingNomineeJustification: whyGoodFit,
      };

      offboardAmbassador.mutate(offboardRequest, {
        onSuccess: () => {
          publishKatalMetric('offboard_ambassador_success', JSON.stringify(offboardRequest));
          setSubmitted(true);
        },
        onError: (err) => {
          publishKatalMetric(
            'offboard_ambassador_error',
            JSON.stringify({ ...offboardRequest, error: err?.message })
          );
          setFailed(true);
          console.error(`Error offboarding ambassador: ${err}`);
        },
      });
    } else {
      setFailed(true);
      console.error(`Error offboarding ambassador: No Person ID found for current user`);
    }
  };

  return (
    <AppLayout
      navigationHide
      toolsOpen={true}
      tools={
        <HelpPanel header={<h2>{t('ambassadorOffboarding.help.beforeYouGo')}</h2>}>
          {<AmbassadorOffboardingHelp />}
        </HelpPanel>
      }
      notifications={
        <>
          <Flashbar
            items={
              failed
                ? [
                    {
                      type: 'error',
                      dismissible: true,
                      content: t('ambassadorOffboarding.error'),
                      id: 'message_1',
                    },
                  ]
                : []
            }
          />
        </>
      }
      content={
        <ContentLayout
          header={
            <Header
              variant="h1"
              description={t('ambassadorOffboarding.thankYouForYourMeaningfulContributions')}
              data-testid="ambassador-offboard-page-header"
            >
              {t('ambassadorOffboarding.leaveTheCommunityAmbassadorProgram')}
            </Header>
          }
        >
          <Container
            header={
              <Header variant="h2" description={t('ambassadorOffboarding.wereSorryToSeeYouGo')}>
                {t('ambassadorOffboarding.leaveTheAmbassadorProgram')}
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="m">
              <FormField label={t('ambassadorOffboarding.whyAreYouLeavingTheProgram')}>
                <SpaceBetween direction="vertical" size={'s'}>
                  <Checkbox
                    onChange={({ detail }) =>
                      setTooTimeConsuming((v) =>
                        v ? undefined : t('ambassadorOffboarding.tooTimeConsuming')
                      )
                    }
                    checked={!!tooTimeConsuming}
                    controlId="ambassador-offboard-reason-time-consuming-checkbox"
                  >
                    {t('ambassadorOffboarding.tooTimeConsuming')}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) =>
                      setRotatesAmbassadors((v) =>
                        v ? undefined : t('ambassadorOffboarding.myTeamRotatesAmbassadors')
                      )
                    }
                    checked={!!rotatesAmbassadors}
                  >
                    {t('ambassadorOffboarding.myTeamRotatesAmbassadors')}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) =>
                      setIDontEnjoyThisRole((v) =>
                        v ? undefined : t('ambassadorOffboarding.iDontEnjoyThisRole')
                      )
                    }
                    checked={!!iDontEnjoyThisRole}
                  >
                    {t('ambassadorOffboarding.iDontEnjoyThisRole')}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) =>
                      setImChangingSiteTeamOrElg((v) =>
                        v ? undefined : t('ambassadorOffboarding.changingSiteTeamOrLeftElg')
                      )
                    }
                    checked={!!imChangingSiteTeamOrElg}
                  >
                    {t('ambassadorOffboarding.changingSiteTeamOrLeftElg')}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) =>
                      setLeavingForOtherReason((v) =>
                        v ? undefined : t('ambassadorOffboarding.other')
                      )
                    }
                    checked={!!leavingForOtherReason}
                  >
                    {t('ambassadorOffboarding.other')}
                  </Checkbox>
                </SpaceBetween>
              </FormField>
              {leavingForOtherReason && (
                <FormField
                  stretch
                  label={t('ambassadorOffboarding.additionalFeedback')}
                  description={t('ambassadorOffboarding.optional')}
                >
                  <Textarea
                    onChange={({ detail }) => setFeedback(detail.value)}
                    value={feedback}
                    placeholder={t('ambassadorOffboarding.tellUsMoreAboutYourExperience')}
                  />
                  <TextContent>
                    <small>{t('ambassadorOffboarding.yourFeedback')}</small>
                  </TextContent>
                </FormField>
              )}

              <div>
                <FormField
                  label={t('ambassadorOffboarding.nominate')}
                  description={t('ambassadorOffboarding.optional')}
                />
                <div
                  className="ambassador-representation-checkbox"
                  data-testid="ambassador-representation-checkbox"
                >
                  <SpaceBetween direction="vertical" size="m">
                    <FormField stretch label={t('ambassadorOffboarding.nomineeName')}>
                      <AmbassadorSelect
                        currentAmbassadors={`[${selectedReplacement?.value}]` || '[]'}
                        onChange={({ detail }) => {
                          setSelectedReplacement(
                            !selectedReplacement
                              ? detail.selectedOptions[0]
                              : detail.selectedOptions[1]
                          );
                        }}
                      />
                    </FormField>
                    <FormField stretch label={t('ambassadorOffboarding.whyWouldTheyBeAGoodFit')}>
                      <Textarea
                        onChange={({ detail }) => setWhyGoodFit(detail.value)}
                        value={whyGoodFit || ''}
                        placeholder={t('ambassadorOffboarding.tellUsAboutYourNominee')}
                      />
                    </FormField>
                  </SpaceBetween>
                </div>
              </div>
              <Button
                data-testid="ambassador-offboard-leave-button"
                variant="normal"
                onClick={onSubmit}
                disabled={!isValid}
                loading={isLoading}
              >
                {t('ambassadorOffboarding.leaveAmbassadorProgram')}
              </Button>
            </SpaceBetween>
          </Container>
        </ContentLayout>
      }
    />
  );
}
